import { Link } from "wouter";
import { Trash2, Plus, Minus, ShoppingCart, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { useCart } from "@/lib/cart";
import { SEO } from "@/components/seo";

export default function CartPage() {
  const { items, removeItem, updateQuantity, totalPrice } = useCart();

  if (items.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <SEO title="Shopping Cart" description="Your shopping cart for Sierra Chart templates and indicators." noIndex />
        <div className="max-w-md mx-auto text-center">
          <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
            <ShoppingCart className="w-8 h-8 text-muted-foreground" />
          </div>
          <h1 className="text-2xl font-bold mb-2" data-testid="text-cart-empty">
            Your Cart is Empty
          </h1>
          <p className="text-muted-foreground mb-6">
            Browse our templates and indicators to find the perfect tools for your trading setup.
          </p>
          <Link href="/">
            <Button data-testid="button-continue-shopping">
              Continue Shopping
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <SEO title="Shopping Cart" description="Review your Sierra Chart templates and indicators before checkout." noIndex />
      <h1 className="text-2xl sm:text-3xl font-bold mb-8" data-testid="text-cart-title">
        Shopping Cart
      </h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-4">
          {items.map((item) => (
            <Card key={item.product.id} data-testid={`card-cart-item-${item.product.id}`}>
              <CardContent className="p-4">
                <div className="flex gap-4">
                  {item.product.image && (
                    <div className="w-24 h-24 sm:w-32 sm:h-20 rounded-md overflow-hidden flex-shrink-0">
                      <img
                        src={item.product.image}
                        alt={item.product.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <Link href={`/product/${item.product.slug}`}>
                      <h3 className="font-semibold cursor-pointer hover:text-primary transition-colors truncate" data-testid={`text-cart-item-name-${item.product.id}`}>
                        {item.product.name}
                      </h3>
                    </Link>
                    <p className="text-sm text-muted-foreground mt-1 line-clamp-1">
                      {item.product.description}
                    </p>
                    <div className="flex items-center justify-between gap-2 mt-3 flex-wrap">
                      <div className="flex items-center gap-2">
                        <Button
                          size="icon"
                          variant="outline"
                          onClick={() =>
                            updateQuantity(item.product.id, item.quantity - 1)
                          }
                          data-testid={`button-decrease-${item.product.id}`}
                        >
                          <Minus className="w-3 h-3" />
                        </Button>
                        <span className="w-8 text-center font-medium" data-testid={`text-quantity-${item.product.id}`}>
                          {item.quantity}
                        </span>
                        <Button
                          size="icon"
                          variant="outline"
                          onClick={() =>
                            updateQuantity(item.product.id, item.quantity + 1)
                          }
                          data-testid={`button-increase-${item.product.id}`}
                        >
                          <Plus className="w-3 h-3" />
                        </Button>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-bold text-primary" data-testid={`text-cart-item-price-${item.product.id}`}>
                          ${(Number(item.product.price) * item.quantity).toFixed(2)}
                        </span>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => removeItem(item.product.id)}
                          data-testid={`button-remove-${item.product.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div>
          <Card>
            <CardContent className="p-6">
              <h2 className="font-semibold text-lg mb-4">Order Summary</h2>

              <div className="space-y-2 mb-4">
                {items.map((item) => (
                  <div key={item.product.id} className="flex items-center justify-between gap-2 text-sm">
                    <span className="text-muted-foreground truncate">
                      {item.product.name} x{item.quantity}
                    </span>
                    <span>${(Number(item.product.price) * item.quantity).toFixed(2)}</span>
                  </div>
                ))}
              </div>

              <Separator className="mb-4" />

              <div className="flex items-center justify-between gap-2 mb-6">
                <span className="font-semibold">Total</span>
                <span className="text-xl font-bold text-primary" data-testid="text-cart-total">
                  ${totalPrice.toFixed(2)}
                </span>
              </div>

              <Link href="/checkout">
                <Button className="w-full" size="lg" data-testid="button-checkout">
                  Proceed to Checkout
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>

              <Link href="/">
                <Button variant="ghost" className="w-full mt-2" data-testid="button-continue-shopping">
                  Continue Shopping
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
